import { callLogApi } from '../api/call-log.api';
import { CallActivityEventType } from '../api/call-log.types';
import { logger } from '../../utils/logger';
import {
  claimOnce,
  getOrCreateSharedCallTraceId,
  readSharedCallTraceId,
} from './cross-tab-call-guard.util';

type TraceState = {
  callTraceId: string;
  answeredLogged: boolean;
  direction?: 'INBOUND' | 'OUTBOUND';
};

/** Active traces keyed by CXone contact id (or a pending key before contact id is known). */
const tracesByContactId = new Map<string, TraceState>();
const PENDING_TRACE_KEY = '__pending__';
let lastActiveContactKey: string | null = null;

/** Events that must appear at most once per CXone contact across all widget tabs. */
const ONCE_PER_CONTACT_EVENTS = new Set<CallActivityEventType>([
  'CALL_RECEIVED',
  'CALL_ANSWERED',
  'CALL_ENDED',
  'DISPOSITION_COMPLETED',
  'SAP_CONTACTS_DISPLAYED',
]);

function createTraceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `trace-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function resolveContactKey(cxoneContactId?: string | null): string {
  return cxoneContactId?.trim() || PENDING_TRACE_KEY;
}

function getOrCreateTrace(contactKey: string): TraceState {
  const existing = tracesByContactId.get(contactKey);
  if (existing) {
    return existing;
  }

  const sharedId =
    contactKey !== PENDING_TRACE_KEY ? getOrCreateSharedCallTraceId(contactKey) : createTraceId();

  const created: TraceState = {
    callTraceId: sharedId,
    answeredLogged: false,
  };
  tracesByContactId.set(contactKey, created);
  return created;
}

function normalizeTraceDirection(
  trace: TraceState,
  payload: Record<string, unknown>
): Record<string, unknown> {
  const payloadDirection = payload.direction;
  if (payloadDirection !== 'INBOUND' && payloadDirection !== 'OUTBOUND') {
    return payload;
  }

  if (!trace.direction) {
    trace.direction = payloadDirection;
    return payload;
  }

  if (payloadDirection === trace.direction) {
    return payload;
  }

  logger.debug('[CallLog] Correcting direction drift within call trace', {
    recordedDirection: trace.direction,
    ignoredDirection: payloadDirection,
  });
  return { ...payload, direction: trace.direction };
}

/**
 * Start (or reuse) a call trace for a contact.
 * A different contact id always gets its own trace so overlapping calls do not merge.
 * Across Agent Desktop tabs, the same cxoneContactId shares one callTraceId via localStorage.
 */
export function beginCallTrace(cxoneContactId?: string | null): string {
  const contactKey = resolveContactKey(cxoneContactId);

  if (contactKey !== PENDING_TRACE_KEY) {
    const pending = tracesByContactId.get(PENDING_TRACE_KEY);
    if (pending && !tracesByContactId.has(contactKey)) {
      // Prefer an already-published shared id when migrating pending → contact.
      const shared = readSharedCallTraceId(contactKey);
      if (shared) {
        pending.callTraceId = shared;
      } else {
        // Publish this tab's pending id as the shared timeline id.
        try {
          localStorage.setItem(
            `xella:call:trace:${contactKey}`,
            JSON.stringify({ id: pending.callTraceId, at: Date.now() })
          );
        } catch {
          // ignore
        }
      }
      tracesByContactId.set(contactKey, pending);
      tracesByContactId.delete(PENDING_TRACE_KEY);
    }
  }

  const trace = getOrCreateTrace(contactKey);
  lastActiveContactKey = contactKey;
  return trace.callTraceId;
}

export function bindCallTraceContact(cxoneContactId: string): void {
  const contactKey = cxoneContactId.trim();
  if (!contactKey) {
    return;
  }

  const pending = tracesByContactId.get(PENDING_TRACE_KEY);
  if (pending && !tracesByContactId.has(contactKey)) {
    const shared = readSharedCallTraceId(contactKey);
    if (shared) {
      pending.callTraceId = shared;
    } else {
      try {
        localStorage.setItem(
          `xella:call:trace:${contactKey}`,
          JSON.stringify({ id: pending.callTraceId, at: Date.now() })
        );
      } catch {
        // ignore
      }
    }
    tracesByContactId.set(contactKey, pending);
    tracesByContactId.delete(PENDING_TRACE_KEY);
  } else if (!tracesByContactId.has(contactKey)) {
    getOrCreateTrace(contactKey);
  }

  lastActiveContactKey = contactKey;
}

export function getCallTraceId(cxoneContactId?: string | null): string | null {
  if (cxoneContactId) {
    return tracesByContactId.get(resolveContactKey(cxoneContactId))?.callTraceId ?? null;
  }

  if (lastActiveContactKey) {
    return tracesByContactId.get(lastActiveContactKey)?.callTraceId ?? null;
  }

  return tracesByContactId.get(PENDING_TRACE_KEY)?.callTraceId ?? null;
}

export function clearCallTrace(cxoneContactId?: string | null): void {
  if (cxoneContactId) {
    const contactKey = resolveContactKey(cxoneContactId);
    tracesByContactId.delete(contactKey);
    if (lastActiveContactKey === contactKey) {
      lastActiveContactKey = null;
    }
    // Keep localStorage claims/trace id so a second Agent Desktop tab cannot
    // re-append once-per-call events or re-run disposition for this contact.
    return;
  }

  tracesByContactId.clear();
  lastActiveContactKey = null;
}

export function logCallAnswered(
  payload: Record<string, unknown>,
  cxoneContactId?: string | null
): void {
  const contactKey = resolveContactKey(cxoneContactId);
  if (cxoneContactId) {
    bindCallTraceContact(cxoneContactId);
  }

  const trace = getOrCreateTrace(contactKey);
  lastActiveContactKey = contactKey;

  if (trace.answeredLogged) {
    return;
  }
  trace.answeredLogged = true;
  logCallActivity('CALL_ANSWERED', payload, cxoneContactId);
}

function shouldSkipDuplicateEvent(
  eventType: CallActivityEventType,
  payload: Record<string, unknown>,
  cxoneContactId?: string | null
): boolean {
  const contactId = cxoneContactId?.trim();
  if (!contactId) {
    return false;
  }

  if (eventType === 'SAP_CONTACT_OPENED') {
    const sapId =
      (payload.contact as { id?: string } | undefined)?.id ||
      (typeof payload.sapContactId === 'string' ? payload.sapContactId : undefined) ||
      'unknown';
    return !claimOnce(`${contactId}:${eventType}:${sapId}`);
  }

  if (ONCE_PER_CONTACT_EVENTS.has(eventType)) {
    return !claimOnce(`${contactId}:${eventType}`);
  }

  return false;
}

export function logCallActivity(
  eventType: CallActivityEventType,
  payload: Record<string, unknown> = {},
  cxoneContactId?: string | null
): void {
  const contactKey = resolveContactKey(cxoneContactId);
  if (cxoneContactId) {
    bindCallTraceContact(cxoneContactId);
  } else if (!tracesByContactId.has(contactKey) && !lastActiveContactKey) {
    beginCallTrace(cxoneContactId);
  }

  const activeKey =
    (cxoneContactId && resolveContactKey(cxoneContactId)) ||
    lastActiveContactKey ||
    PENDING_TRACE_KEY;
  const trace = getOrCreateTrace(activeKey);
  lastActiveContactKey = activeKey;
  const normalizedPayload = normalizeTraceDirection(trace, payload);

  if (shouldSkipDuplicateEvent(eventType, normalizedPayload, cxoneContactId)) {
    logger.debug('[CallLog] Skipping duplicate event from another Agent Desktop tab', {
      eventType,
      cxoneContactId,
    });
    return;
  }

  void callLogApi
    .append({
      callTraceId: trace.callTraceId,
      cxoneContactId: cxoneContactId ?? (activeKey !== PENDING_TRACE_KEY ? activeKey : undefined),
      eventType,
      payload: normalizedPayload,
    })
    .catch((error: unknown) => {
      logger.debug('[CallLog] Failed to append activity event (non-blocking)', {
        eventType,
        error: error instanceof Error ? error.message : String(error),
      });
    });
}
